"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@/lib/utils/validation/zod-react-hook-form-resolver";
import * as z from "zod";
import { Message } from "@/lib/core/crm/types";
import { ComposeFormValues, ComposeTab, VariableValues } from "@/lib/types/compose-modal-types";
import { COMPOSE_LAST_FROM_ACCOUNT_KEY } from "@/lib/hooks/compose/useCompose";

/**
 * Sticky per-user default for the "track opens" checkbox (remembered per device,
 * overridable per email). Defaults OFF — 1:1 open pixels are opt-in for
 * deliverability/privacy reasons (see the timeline "opened" badge tooltip).
 */
export const COMPOSE_TRACK_OPENS_KEY = "compose.trackOpens.default";

export function readTrackOpensDefault(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COMPOSE_TRACK_OPENS_KEY) === "true";
  } catch {
    return false;
  }
}

const formSchema = z
  .object({
    to: z
      .string()
      .min(1, "At least one recipient is required")
      .refine((value) => {
        const emails = value.split(",").map((email) => email.trim());
        return emails.every((email) => z.string().email().safeParse(email).success);
      }, "Invalid email address(es)"),
    cc: z
      .string()
      .optional()
      .refine((value) => {
        if (!value || value.trim() === "") return true;
        const emails = value.split(",").map((email) => email.trim());
        return emails.every((email) => z.string().email().safeParse(email).success);
      }, "Invalid CC email address(es)"),
    bcc: z
      .string()
      .optional()
      .refine((value) => {
        if (!value || value.trim() === "") return true;
        const emails = value.split(",").map((email) => email.trim());
        return emails.every((email) => z.string().email().safeParse(email).success);
      }, "Invalid BCC email address(es)"),
    subject: z.string().min(1, "Subject is required"),
    body: z.string().min(1, "Body is required"),
    fromAccount: z.string().min(1, "Please select an account to send from"),
    signature: z.string().optional(),
    trackOpens: z.boolean().optional(),
    attachments: z
      .array(
        z.object({
          id: z.string(),
          filename: z.string(),
          contentType: z.string(),
          content: z.string(),
          size: z.number(),
        })
      )
      .optional(),
  })
  .refine(
    (data) => {
      const toEmails = data.to ? data.to.split(",").map((email) => email.trim().toLowerCase()) : [];
      const ccEmails = data.cc ? data.cc.split(",").map((email) => email.trim().toLowerCase()) : [];
      const bccEmails = data.bcc
        ? data.bcc.split(",").map((email) => email.trim().toLowerCase())
        : [];
      const allEmails = [...toEmails, ...ccEmails, ...bccEmails];
      const uniqueEmails = new Set(allEmails);
      return allEmails.length === uniqueEmails.size;
    },
    {
      message: "Duplicate email addresses found across To, CC, and BCC fields",
      path: ["to"],
    }
  );

interface UseComposeFormStateProps {
  isOpen: boolean;
  initialSubject?: string;
  initialBody?: string;
}

/**
 * Hook for managing compose form state
 * Handles form initialization, validation, and watched values
 */
export function useComposeFormState({
  isOpen,
  initialSubject,
  initialBody,
}: UseComposeFormStateProps) {
  const [activeTab, setActiveTab] = useState<ComposeTab>("compose");
  const [hasUnfilledVariables, setHasUnfilledVariables] = useState(false);
  const [variableValues, setVariableValues] = useState<VariableValues>({});
  const [showVariablePreview, setShowVariablePreview] = useState(false);

  const form = useForm<ComposeFormValues>({
    resolver: zodResolver(formSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      to: "",
      cc: "",
      bcc: "",
      subject: initialSubject || "",
      body: initialBody || "",
      fromAccount: localStorage.getItem(COMPOSE_LAST_FROM_ACCOUNT_KEY) || "",
      signature: "",
      attachments: [],
      trackOpens: readTrackOpensDefault(),
    },
  });

  const watchedSubject = form.watch("subject");
  const watchedBody = form.watch("body");

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      form.reset({
        to: "",
        cc: "",
        bcc: "",
        subject: initialSubject || "",
        body: initialBody || "",
        fromAccount: localStorage.getItem(COMPOSE_LAST_FROM_ACCOUNT_KEY) || "",
        signature: "",
        attachments: [],
        trackOpens: readTrackOpensDefault(),
      });
    }
  }, [isOpen, initialSubject, initialBody, form]);

  const handleTabChange = useCallback((tab: ComposeTab) => {
    setActiveTab(tab);
  }, []);

  const handleVariableUpdate = useCallback((variables: VariableValues) => {
    setVariableValues(variables);
  }, []);

  const handleToggleVariablePreview = useCallback(() => {
    setShowVariablePreview((prev) => !prev);
  }, []);

  return useMemo(
    () => ({
      form,
      activeTab,
      hasUnfilledVariables,
      variableValues,
      showVariablePreview,
      watchedSubject,
      watchedBody,
      setActiveTab,
      setHasUnfilledVariables,
      setVariableValues,
      setShowVariablePreview,
      handleTabChange,
      handleVariableUpdate,
      handleToggleVariablePreview,
    }),
    [
      form,
      activeTab,
      hasUnfilledVariables,
      variableValues,
      showVariablePreview,
      watchedSubject,
      watchedBody,
      handleTabChange,
      handleVariableUpdate,
      handleToggleVariablePreview,
    ]
  );
}
